<template>
    <div class="modal-json-wrap">
        <div class="json-toolbar">
            <input v-model="search" type="text" placeholder="Find in JSON…" class="json-search-input" />
            <span class="json-match-count">{{ matchCount }}</span>
            <button v-if="search" @click="search = ''" class="json-search-clear" title="Clear">&times;</button>
        </div>
        <pre class="json-pre" v-html="highlighted"></pre>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import api from '@/api'

const props = defineProps<{ dealId: number }>()

const rawJson = ref('Loading…')
const search = ref('')

onMounted(async () => {
    try {
        const resp = await api.getDealJson(props.dealId)
        const raw = typeof resp.data === 'string' ? resp.data : JSON.stringify(resp.data)
        try { rawJson.value = JSON.stringify(JSON.parse(raw), null, 2) } catch { rawJson.value = raw }
    } catch (err: any) {
        rawJson.value = `Error loading JSON: ${err?.message ?? err}`
    }
})

const escapeHtml = (s: string) =>
    s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!))
const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const highlighted = computed(() => {
    const escaped = escapeHtml(rawJson.value)
    const q = search.value.trim()
    if (!q) return escaped
    const re = new RegExp(escapeRegex(escapeHtml(q)), 'gi')
    return escaped.replace(re, m => `<mark class="json-hit">${m}</mark>`)
})

const matchCount = computed(() => {
    const q = search.value.trim()
    if (!q) return ''
    const re = new RegExp(escapeRegex(escapeHtml(q)), 'gi')
    const matches = escapeHtml(rawJson.value).match(re)
    return matches ? `${matches.length} match${matches.length === 1 ? '' : 'es'}` : 'no matches'
})
</script>

<style scoped>
.modal-json-wrap { display: flex; flex-direction: column; height: 100%; }
.json-toolbar {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    background: #263238;
    border-bottom: 1px solid #37474f;
    flex-shrink: 0;
}
.json-search-input {
    padding: 4px 8px;
    font-size: 12px;
    border: 1px solid #546e7a;
    border-radius: 3px;
    background: #37474f;
    color: #fff;
    min-width: 220px;
}
.json-search-input::placeholder { color: #90a4ae; }
.json-search-input:focus { outline: none; border-color: #80cbc4; }
.json-match-count { font-size: 11px; color: #90a4ae; white-space: nowrap; min-width: 60px; }
.json-search-clear {
    background: none;
    border: none;
    font-size: 16px;
    color: #90a4ae;
    cursor: pointer;
    padding: 0 4px;
    line-height: 1;
}
.json-search-clear:hover { color: #fff; }
.json-pre {
    flex: 1;
    margin: 0;
    padding: 12px 16px;
    color: #aed581;
    font-family: 'Courier New', monospace;
    font-size: 12px;
    white-space: pre-wrap;
    word-wrap: break-word;
    line-height: 1.5;
    background: #1e1e1e;
    overflow-y: auto;
}
:deep(.json-hit) {
    background: #ffeb3b;
    color: #000;
    padding: 0 1px;
    border-radius: 2px;
}
</style>
